import React, { useState, useEffect, useMemo } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from './Dialog';
import { Button } from './Button';
import { Input } from './Input';
import { Label } from './Label';
import { Textarea } from './Textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './Select';
import { PersonaIconPicker } from './PersonaIconPicker';
import { PersonaWithSettings } from '../endpoints/personas/list_GET.schema';
import { PersonaCategoryArrayValues } from '../helpers/schema';
import styles from './PersonaCreateDialog.module.css';

interface PersonaCreateDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  editingPersona?: PersonaWithSettings | null;
  existingCategories?: string[];
  onSubmit: (data: {
    name: string;
    description?: string;
    icon: string;
    category: string;
    systemPrompt: string;
    priority: number;
    tags?: string[];
  }) => void;
  isSubmitting?: boolean;
}

export const PersonaCreateDialog: React.FC<PersonaCreateDialogProps> = ({
  open,
  onOpenChange,
  editingPersona,
  existingCategories = [],
  onSubmit,
  isSubmitting,
}) => {
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [icon, setIcon] = useState('sparkles');
  const [category, setCategory] = useState<string>('custom');
  const [customCategory, setCustomCategory] = useState('');
  const [isCustomEntry, setIsCustomEntry] = useState(false);
  const [systemPrompt, setSystemPrompt] = useState('');
  const [priority, setPriority] = useState(50);
  const [tagsInput, setTagsInput] = useState('');

  // Merge built-in + existing categories from data
  const allCategories = useMemo(() => {
    const builtIn = new Set(PersonaCategoryArrayValues as string[]);
    const all = new Set([...builtIn, ...existingCategories]);
    return Array.from(all).sort((a, b) => {
      // Built-ins first, then alphabetical
      const aBuiltin = builtIn.has(a) ? 0 : 1;
      const bBuiltin = builtIn.has(b) ? 0 : 1;
      if (aBuiltin !== bBuiltin) return aBuiltin - bBuiltin;
      return a.localeCompare(b);
    });
  }, [existingCategories]);

  const categoryLabels: Record<string, string> = {
    development: 'Development',
    security: 'Security',
    operations: 'Operations',
    documentation: 'Documentation',
    compliance: 'Compliance',
    governance: 'Governance',
    custom: 'Custom',
  };

  useEffect(() => {
    if (editingPersona) {
      setName(editingPersona.name);
      setDescription(editingPersona.description || '');
      setIcon(editingPersona.icon);
      const cat = editingPersona.category;
      if (allCategories.includes(cat)) {
        setCategory(cat);
        setIsCustomEntry(false);
      } else {
        setCategory('__new__');
        setCustomCategory(cat);
        setIsCustomEntry(true);
      }
      setSystemPrompt(editingPersona.systemPrompt);
      setPriority(editingPersona.priority);
      setTagsInput(editingPersona.tags?.join(', ') || '');
    } else {
      setName('');
      setDescription('');
      setIcon('sparkles');
      setCategory('custom');
      setCustomCategory('');
      setIsCustomEntry(false);
      setSystemPrompt('');
      setPriority(50);
      setTagsInput('');
    }
  }, [editingPersona, open, allCategories]);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const tags = tagsInput
      .split(',')
      .map((t) => t.trim())
      .filter((t) => t.length > 0);

    const finalCategory = isCustomEntry
      ? customCategory.trim().toLowerCase().replace(/\s+/g, '-')
      : category;

    if (!finalCategory) return;

    onSubmit({
      name,
      description: description || undefined,
      icon,
      category: finalCategory,
      systemPrompt,
      priority,
      tags: tags.length > 0 ? tags : undefined,
    });
  };

  const handleCategoryChange = (value: string) => {
    if (value === '__new__') {
      setIsCustomEntry(true);
      setCategory('__new__');
    } else {
      setIsCustomEntry(false);
      setCategory(value);
      setCustomCategory('');
    }
  };

  const formatLabel = (cat: string) =>
    categoryLabels[cat] || cat.split('-').map((w) => w.charAt(0).toUpperCase() + w.slice(1)).join(' ');

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className={styles.dialog}>
        <DialogHeader>
          <DialogTitle>{editingPersona ? 'Edit Persona' : 'Create Custom Persona'}</DialogTitle>
          <DialogDescription>
            {editingPersona
              ? 'Update your custom persona settings and rules.'
              : 'Create a new AI persona with custom behavior rules.'}
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className={styles.form}>
          <div className={styles.formGrid}>
            <div className={styles.field}>
              <Label htmlFor="name">Name</Label>
              <Input
                id="name"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="e.g., Security Reviewer"
                required
                maxLength={100}
              />
            </div>

            <div className={styles.field}>
              <Label htmlFor="category">Category</Label>
              <Select value={isCustomEntry ? '__new__' : category} onValueChange={handleCategoryChange}>
                <SelectTrigger>
                  <SelectValue placeholder="Select category" />
                </SelectTrigger>
                <SelectContent>
                  {allCategories.map((cat) => (
                    <SelectItem key={cat} value={cat}>
                      {formatLabel(cat)}
                    </SelectItem>
                  ))}
                  <SelectItem value="__new__">+ New Category...</SelectItem>
                </SelectContent>
              </Select>
              {isCustomEntry && (
                <Input
                  value={customCategory}
                  onChange={(e) => setCustomCategory(e.target.value)}
                  placeholder="e.g., data-science, design, marketing"
                  maxLength={50}
                  required
                  style={{ marginTop: 6 }}
                />
              )}
            </div>
          </div>

          <div className={styles.field}>
            <Label htmlFor="description">Description (optional)</Label>
            <Input
              id="description"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Brief description of this persona"
              maxLength={500}
            />
          </div>

          <div className={styles.field}>
            <Label>Icon</Label>
            <PersonaIconPicker value={icon} onChange={setIcon} />
          </div>

          <div className={styles.field}>
            <Label htmlFor="systemPrompt">System Prompt / Rules</Label>
            <Textarea
              id="systemPrompt"
              value={systemPrompt}
              onChange={(e) => setSystemPrompt(e.target.value)}
              placeholder="Enter the behavior rules and guidelines for this persona..."
              required
              minLength={10}
              maxLength={5000}
              className={styles.textarea}
            />
            <span className={styles.charCount}>{systemPrompt.length}/5000</span>
          </div>

          <div className={styles.formGrid}>
            <div className={styles.field}>
              <Label htmlFor="priority">Priority (1-100)</Label>
              <Input
                id="priority"
                type="number"
                value={priority}
                onChange={(e) => setPriority(Number(e.target.value))}
                min={1}
                max={100}
              />
              <span className={styles.hint}>Lower = higher priority</span>
            </div>

            <div className={styles.field}>
              <Label htmlFor="tags">Tags (comma-separated)</Label>
              <Input
                id="tags"
                value={tagsInput}
                onChange={(e) => setTagsInput(e.target.value)}
                placeholder="e.g., review, security, best-practices"
              />
            </div>
          </div>

          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
              Cancel
            </Button>
            <Button type="submit" disabled={isSubmitting || !name || systemPrompt.length < 10 || (isCustomEntry && !customCategory.trim())}>
              {isSubmitting ? 'Saving...' : editingPersona ? 'Update Persona' : 'Create Persona'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};
